import { c as _c } from "react/compiler-runtime";
import type { AIReviewInstallationSettingsResponse } from '@/app/api/ai-review-settings/[installationId]/global/types';
import type { AIReviewSettings } from '@/lib/ai-review-settings/types';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AIReviewSettingsQueryKeyOptions } from './query-key';
import { DEFAULT_FETCH_ERROR, getAIReviewInstallationSettings, SettingsFetchError, shouldRetrySettingsFetch } from './api-client';
import { aiReviewSettingsQueryKey } from './query-key';
import { AI_REVIEW_SETTINGS_ROUTE } from '@/app/api/ai-review-settings/[installationId]/[repositoryId]/constants';
import { DEFAULT_AUTO_APPROVE_BEHAVIOR, DEFAULT_AUTO_APPROVE_MODE } from '@/lib/ai-review-settings/auto-approve';
import { DEFAULT_AUTO_ULTRAREVIEW_MODE } from '@/lib/ai-review-settings/auto-ultrareview';
import { DEFAULT_AUTO_APPROVE_ALLOWED_OPEN_ISSUES, DEFAULT_AUTO_APPROVE_EXCLUDE_EXTERNAL_CONTRIBUTORS, getDefaultReviewSensitivityForRepository } from '@/lib/ai-review-settings/defaults';
import { AiReviewSettingsResponseSchema, DEFAULT_AUTO_FIX_LOOP_MODE, DEFAULT_AUTO_FIX_PROVIDER, DEFAULT_VERIFY_MODE } from '@/lib/ai-review-settings/schema';
import { DEFAULT_ULTRAREVIEW_MODE } from '@/lib/ai-review-settings/ultrareview-mode';

/**
 * Client-side fallback values for AI review settings when no persisted row has
 * been read yet. Keep these aligned with prisma/schema.prisma and the server's
 * getDefaultAIReviewSettingValues helper; they let the UI render an editable
 * first-write state while the PATCH endpoint still enforces access and upserts.
 */
export function getDefaultAIReviewSettings(installationId: number, repositoryId: number, options: {
  repositoryIsPrivate?: boolean;
} = {}): AIReviewSettings {
  const sensitivity = options.repositoryIsPrivate === undefined ? 'medium' : getDefaultReviewSensitivityForRepository({
    isPrivate: options.repositoryIsPrivate
  });
  return {
    installationId,
    repositoryId,
    generalReviewEnabled: true,
    checkDrafts: false,
    generatePrDescriptionEnabled: true,
    prDescriptionReviewLinkEnabled: true,
    skipPrDescriptionIfAuthorProvided: false,
    incrementalReviewsEnabled: true,
    architectureDiagramsEnabled: false,
    externalContributorsRequireManualReview: false,
    sensitivity,
    autoApproveBehavior: DEFAULT_AUTO_APPROVE_BEHAVIOR,
    autoApproveMode: DEFAULT_AUTO_APPROVE_MODE,
    autoApproveExcludeExternalContributors: DEFAULT_AUTO_APPROVE_EXCLUDE_EXTERNAL_CONTRIBUTORS,
    autoApproveAllowedOpenIssues: DEFAULT_AUTO_APPROVE_ALLOWED_OPEN_ISSUES,
    ultrareviewMode: DEFAULT_ULTRAREVIEW_MODE,
    autoUltrareviewMode: DEFAULT_AUTO_ULTRAREVIEW_MODE,
    autoUltrareviewCustomPrompt: null,
    autoUltrareviewFilePatterns: null,
    mergeConfidenceEnabled: false,
    autoFixButtonEnabled: true,
    autoFixProvider: DEFAULT_AUTO_FIX_PROVIDER,
    prCommentFixesEnabled: true,
    fixCommitsToPr: true,
    autoFixLoopMode: DEFAULT_AUTO_FIX_LOOP_MODE,
    autoFixLoopFilters: null,
    verifyMode: DEFAULT_VERIFY_MODE,
    verifyFilters: null,
    verifyInstructions: null,
    codingAgentInstructions: null,
    maxChangedLines: null,
    resolveThreadsWhenAddressed: true,
    customInstructions: null,
    summaryInstructions: null,
    documentationLinks: null,
    ignorePatterns: null,
    includePatterns: null,
    autoApproveExcludePatterns: null,
    autoApproveIncludePatterns: null,
    autoApproveFilters: null,
    ignoreBranchPatterns: null,
    ignoreBaseBranchPatterns: null,
    ignoreLabels: null,
    ignoreTitlePatterns: null
  };
}
export const ALL_REPOSITORIES_SETTINGS_ROW_REPOSITORY_ID = 0;
export function useQueryAIReviewSettings(props) {
  const $ = _c(24);
  const queryClient = useQueryClient();
  const isGlobalSelection = props.repositoryId === null;
  const repositoryIsPrivate = typeof props.repositoryIsPrivate === "boolean" ? props.repositoryIsPrivate : undefined;
  let t0;
  if ($[0] !== isGlobalSelection || $[1] !== props.installationId || $[2] !== props.repositoryId || $[3] !== repositoryIsPrivate) {
    t0 = aiReviewSettingsQueryKey(props.installationId, props.repositoryId, {
      ...(isGlobalSelection ? {} : {
        repositoryIsPrivate: repositoryIsPrivate ?? null
      })
    });
    $[0] = isGlobalSelection;
    $[1] = props.installationId;
    $[2] = props.repositoryId;
    $[3] = repositoryIsPrivate;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const key = t0;
  let t1;
  if ($[5] !== isGlobalSelection || $[6] !== props.installationId || $[7] !== props.repositoryId || $[8] !== repositoryIsPrivate) {
    t1 = async t2 => {
      const {
        signal
      } = t2;
      if (isGlobalSelection) {
        return getAIReviewInstallationSettings(props.installationId, signal);
      }
      const fetchedSettings = await aiReviewSettingsQueryFn(props.installationId, props.repositoryId ?? null, {
        signal
      });
      if (fetchedSettings || !props.repositoryId || repositoryIsPrivate === undefined) {
        return fetchedSettings;
      }
      return getDefaultAIReviewSettings(props.installationId, props.repositoryId, {
        repositoryIsPrivate
      });
    };
    $[5] = isGlobalSelection;
    $[6] = props.installationId;
    $[7] = props.repositoryId;
    $[8] = repositoryIsPrivate;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const t2 = (props.enabled ?? true) && props.repositoryId !== undefined && Boolean(props.installationId && (isGlobalSelection || props.repositoryId));
  let t3;
  if ($[10] !== key || $[11] !== queryClient) {
    t3 = () => queryClient.getQueryData(key);
    $[10] = key;
    $[11] = queryClient;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== key || $[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
    t4 = {
      queryKey: key,
      queryFn: t1,
      enabled: t2,
      retry: shouldRetrySettingsFetch,
      retryDelay: _temp,
      placeholderData: t3
    };
    $[13] = key;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const {
    data,
    error,
    isFetching,
    isLoading
  } = useQuery(t4);
  const installationState = isGlobalSelection && data && "persisted" in data ? data : null;
  const settings = installationState?.settings ?? data as AIReviewSettings | null | undefined;
  const t5 = installationState?.persisted ?? null;
  let t6;
  if ($[18] !== error || $[19] !== isFetching || $[20] !== isLoading || $[21] !== settings || $[22] !== t5) {
    t6 = {
      settings,
      installationDefaultsPersisted: t5,
      isLoading,
      isFetching,
      error
    };
    $[18] = error;
    $[19] = isFetching;
    $[20] = isLoading;
    $[21] = settings;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}

/**
 * Direct fetcher for a single repository's settings. Returns null when ids are missing/404.
 */
function _temp(attemptIndex) {
  return Math.min(2000 * 2 ** attemptIndex, 30000);
}
export async function aiReviewSettingsQueryFn(installationId: number | null, repositoryId: number | null, options: {
  signal?: AbortSignal;
} = {}): Promise<AIReviewSettings | null> {
  if (!installationId || !repositoryId) {
    console.log('[useAIReviewSettings] Missing installationId or repositoryId');
    return null;
  }
  const response = await fetch(`${AI_REVIEW_SETTINGS_ROUTE}/${installationId}/${repositoryId}`, {
    method: 'GET',
    credentials: 'include',
    signal: options.signal,
    headers: {
      Accept: 'application/json'
    }
  });
  if (response.status === 404) {
    return null;
  }
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch (error) {
    console.error('[useAIReviewSettings] Failed to parse response payload', error);
    throw new SettingsFetchError(DEFAULT_FETCH_ERROR, response.status);
  }
  if (!response.ok) {
    const payloadError = payload && typeof payload === 'object' && 'error' in payload ? (payload as any).error : null;
    const message = typeof payloadError === 'string' ? payloadError : typeof payloadError === 'object' && payloadError !== null && 'message' in payloadError ? (payloadError as any).message as string | undefined : undefined;
    if (message) {
      console.error('[useAIReviewSettings] Fetch failed:', message);
    }
    throw new SettingsFetchError(message ?? DEFAULT_FETCH_ERROR, response.status);
  }
  return AiReviewSettingsResponseSchema.parse(payload);
}

/**
 * Warms the cache for a repo's settings if there is no successful or in-flight query.
 */
export async function prefetchAIReviewSettings(queryClient: QueryClient, installationId: number | null, repositoryId: number | null, options?: AIReviewSettingsQueryKeyOptions) {
  if (!installationId || !repositoryId) {
    return;
  }
  const key = aiReviewSettingsQueryKey(installationId, repositoryId, options);
  const existingState = queryClient.getQueryState(key);
  if (existingState?.fetchStatus === 'fetching' || existingState?.status === 'success') {
    return;
  }
  await queryClient.prefetchQuery({
    queryKey: key,
    queryFn: () => aiReviewSettingsQueryFn(installationId, repositoryId),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}